import { Box, Center, HStack, IconButton, VStack, Wrap, WrapItem, Text } from '@chakra-ui/react'
import { ArrowUp, X } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import SlashCommandMenu, {
  type SlashCommandResult,
  type SlashCommandKey,
  SLASH_COMMANDS,
} from './SlashCommandMenu'

const CMD_KEYS = Object.keys(SLASH_COMMANDS)
// "/<command> <query>" — a full command word followed by a space + query.
const SEARCH_RE = new RegExp(`(?:^|\\s)/(${CMD_KEYS.join('|')})\\s+(.*)$`, 'i')
// A bare "/<partial>" being typed at the end of the input → command palette.
const PALETTE_RE = /(?:^|\s)\/([a-z]*)$/i

// Rotating placeholder suggestions (ChatGPT-style) — varied prompts, light on slashes.
const DEFAULT_SUGGESTIONS = [
  'Ask Octopus AI anything…',
  "Map a seller's network and connections",
  'Find sellers sharing a phone or email',
  'Trace a website back to its owner',
  'Investigate a suspected counterfeit operation',
  'Summarize what we know about a brand',
  'Type / to reference a seller, item or site',
]

// A picked entity, shown as a reference chip above the input.
interface EntityReference {
  type: SlashCommandKey
  prefix: string
  id: string
  title: string
}

// Ocean AI gradient colors (matching the kraken icon)
const KRAKEN_GRADIENT = 'linear-gradient(135deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)'
const KRAKEN_PRIMARY = '#7C3AED'
const KRAKEN_SECONDARY = '#A855F7'

interface KrakenChatBarProps {
  onSendMessage: (message: string) => void
  /**
   * Hard lock — the bar is unusable (e.g. the graph isn't indexed yet).
   * Streaming is NOT a hard lock: use `isStreaming` so the user can keep
   * typing and can stop the reply.
   */
  disabled?: boolean
  /** A reply is streaming → the send button becomes a stop button. */
  isStreaming?: boolean
  /** Abort the in-flight reply. Required for the stop button to do anything. */
  onStop?: () => void
  onFocusChange?: (isFocused: boolean) => void
  placeholderSuggestions?: string[]
}

const KrakenChatBar = ({
  onSendMessage,
  disabled = false,
  isStreaming = false,
  onStop,
  onFocusChange,
  placeholderSuggestions,
}: KrakenChatBarProps) => {
  const suggestions = placeholderSuggestions ?? DEFAULT_SUGGESTIONS
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [slashCommand, setSlashCommand] = useState<SlashCommandKey | null>(null)
  const [slashQuery, setSlashQuery] = useState('')
  const [paletteQuery, setPaletteQuery] = useState<string | null>(null)
  const [references, setReferences] = useState<EntityReference[]>([])
  const [phIdx, setPhIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Rotate the placeholder while the input is empty and unused.
  useEffect(() => {
    if (inputValue || references.length) return
    const t = setInterval(() => setPhIdx((i) => (i + 1) % suggestions.length), 3200)
    return () => clearInterval(t)
  }, [inputValue, references.length])

  const buildMessage = () => {
    const text = inputValue.trim()
    if (!references.length) return text
    // Passive "References:" block instead of an imperative "Investigate <ref>"
    // prefix — the referenced entities are context for the prompt, not a
    // command, so Octopus no longer auto-runs the default investigation.
    // The `<prefix>_<id>` token is still present so parseEntityQuery can pick
    // it up and start the graph deterministically.
    const refBlock = `References:\n${references.map((r) => `- ${r.prefix}_${r.id} (${r.title})`).join('\n')}`
    return [text, refBlock].filter(Boolean).join('\n\n')
  }

  const canSubmit = (!!inputValue.trim() || references.length > 0) && !disabled

  const closeMenus = () => {
    setSlashCommand(null)
    setSlashQuery('')
    setPaletteQuery(null)
  }

  const handleSubmit = () => {
    const message = buildMessage()
    if (!message || disabled) return
    // Sending while a reply streams implicitly stops it — the parent aborts and
    // keeps the partial text as a finished turn before starting the new one.
    onSendMessage(message)
    setInputValue('')
    setReferences([])
    closeMenus()
  }

  // The one button is send-or-stop. While streaming it always stops, even with
  // text in the box: stopping is the safer read of a click mid-reply, and the
  // draft survives because we don't clear the input.
  const handleButtonClick = () => {
    if (isStreaming) {
      onStop?.()
      return
    }
    handleSubmit()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If a slash menu is open, let it handle Enter (its global keydown listener).
      if (!slashCommand && paletteQuery === null) handleSubmit()
    } else if (e.key === 'Backspace' && !inputValue && references.length > 0) {
      // Backspace on an empty input pops the last reference chip.
      e.preventDefault()
      setReferences((prev) => prev.slice(0, -1))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)

    const searchMatch = val.match(SEARCH_RE)
    if (searchMatch) {
      setSlashCommand(searchMatch[1].toLowerCase() as SlashCommandKey)
      setSlashQuery(searchMatch[2] || '')
      setPaletteQuery(null)
      return
    }
    const paletteMatch = val.match(PALETTE_RE)
    if (paletteMatch) {
      setPaletteQuery(paletteMatch[1].toLowerCase())
      setSlashCommand(null)
      setSlashQuery('')
      return
    }
    closeMenus()
  }

  // Palette: user picked a command → expand the typed "/<partial>" to "/<key> ".
  const handleSelectCommand = (key: SlashCommandKey) => {
    setInputValue((prev) => prev.replace(/\/[a-z]*$/i, `/${key} `))
    setSlashCommand(key)
    setPaletteQuery(null)
    setSlashQuery('')
    inputRef.current?.focus()
  }

  // Search: user picked an entity → add a reference chip, strip the slash text.
  const handleSlashSelect = (result: SlashCommandResult) => {
    const cmd = SLASH_COMMANDS[result.type]
    setReferences((prev) =>
      prev.some((r) => r.prefix === cmd.seedPrefix && r.id === result.id)
        ? prev
        : [...prev, { type: result.type, prefix: cmd.seedPrefix, id: result.id, title: result.title }],
    )
    setInputValue((prev) => prev.replace(SEARCH_RE, '').replace(/\/[a-z]*$/i, '').trimStart())
    closeMenus()
    inputRef.current?.focus()
  }

  const removeReference = (idx: number) => {
    setReferences((prev) => prev.filter((_, i) => i !== idx))
    inputRef.current?.focus()
  }

  const handleFocus = () => {
    setIsFocused(true)
    onFocusChange?.(true)
  }
  const handleBlur = () => {
    setIsFocused(false)
    onFocusChange?.(false)
  }

  const chatBarShadow = '0 0 0 1px rgba(0, 0, 0, 0.06), 0 2px 8px -2px rgba(0, 0, 0, 0.08)'
  const chatBarHoverShadow = '0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 16px -4px rgba(0, 0, 0, 0.10)'
  const chatBarFocusShadow = '0 0 0 1.5px rgba(100, 100, 100, 0.2), 0 4px 16px -4px rgba(0, 0, 0, 0.10)'
  const chatBarShadowMobile = '0 1px 0 rgba(255,255,255,0.9) inset, 0 1px 2px rgba(124, 58, 237, 0.06), 0 8px 24px -8px rgba(124, 58, 237, 0.18)'
  const chatBarFocusShadowMobile = '0 0 0 2px rgba(124, 58, 237, 0.18), 0 8px 24px -6px rgba(124, 58, 237, 0.22)'

  return (
    <Box
      w="100%"
      px={{ base: 3, md: 4 }}
      pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', md: 5 }}
      pt={2}
      flexShrink={0}
      position="relative"
      zIndex={10}
      bg="transparent"
    >
      <style>{`
        .kraken-input::placeholder { color: #a0a0a0; }
        @media (max-width: 767px) { .kraken-input { font-size: 16px !important; } }
      `}</style>
      <Center>
        <VStack gap={0} w="100%" maxW="680px" position="relative">
          <SlashCommandMenu
            command={slashCommand}
            searchQuery={slashQuery}
            paletteQuery={paletteQuery}
            onSelect={handleSlashSelect}
            onSelectCommand={handleSelectCommand}
            onClose={closeMenus}
          />
          <Box
            w="100%"
            borderRadius="22px"
            bg={{ base: 'rgba(255, 255, 255, 0.78)', md: 'white' }}
            backdropFilter={{ base: 'saturate(180%) blur(20px)', md: 'none' }}
            sx={{ WebkitBackdropFilter: 'saturate(180%) blur(20px)' }}
            boxShadow={{
              base: isFocused ? chatBarFocusShadowMobile : chatBarShadowMobile,
              md: isFocused ? chatBarFocusShadow : chatBarShadow,
            }}
            _hover={{
              boxShadow: {
                base: isFocused ? chatBarFocusShadowMobile : chatBarShadowMobile,
                md: isFocused ? chatBarFocusShadow : chatBarHoverShadow,
              },
            }}
            transition="box-shadow 0.3s ease, background 0.2s ease"
            border="1px solid"
            borderColor={{
              base: isFocused ? 'rgba(124, 58, 237, 0.28)' : 'rgba(124, 58, 237, 0.12)',
              md: isFocused ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.08)',
            }}
            px={4}
            py={2}
            pointerEvents="auto"
            onClick={() => inputRef.current?.focus()}
          >
            {/* Reference chips (picked via /command) sit above the input. */}
            {references.length > 0 && (
              <Wrap spacing={1.5} mb={2} mt={0.5}>
                {references.map((ref, idx) => {
                  const Icon = SLASH_COMMANDS[ref.type]?.icon
                  return (
                    <WrapItem key={`${ref.prefix}_${ref.id}`}>
                      <HStack
                        gap={1.5}
                        pl={2}
                        pr={1}
                        py="3px"
                        borderRadius="8px"
                        bg="purple.50"
                        border="1px solid"
                        borderColor="purple.100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {Icon && <Box as={Icon} w="13px" h="13px" color="purple.500" strokeWidth={2.2} />}
                        <Text fontSize="12px" fontWeight={600} color="purple.700" maxW="200px" lineClamp={1}>
                          {ref.title}
                        </Text>
                        <Box
                          as="button"
                          aria-label="Remove reference"
                          onClick={() => removeReference(idx)}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          w="16px"
                          h="16px"
                          borderRadius="full"
                          color="purple.400"
                          _hover={{ bg: 'purple.100', color: 'purple.700' }}
                        >
                          <Box as={X} w="11px" h="11px" strokeWidth={2.5} />
                        </Box>
                      </HStack>
                    </WrapItem>
                  )
                })}
              </Wrap>
            )}

            <HStack w="100%" gap={3} align="center">
              <input
                ref={inputRef}
                type="text"
                className="kraken-input"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={
                  isStreaming
                    ? 'Type to interrupt and ask something else…'
                    : references.length
                      ? 'Add a question, or send to investigate…'
                      : suggestions[phIdx]
                }
                disabled={disabled}
                style={{
                  flex: 1,
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#1a1a1a',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  height: '40px',
                  width: '100%',
                }}
              />
              <SendButton
                onClick={handleButtonClick}
                disabled={isStreaming ? false : !canSubmit}
                isStreaming={isStreaming}
              />
            </HStack>
          </Box>
        </VStack>
      </Center>
    </Box>
  )
}

interface SendButtonProps {
  onClick: () => void
  disabled: boolean
  /** Streaming → renders as a stop button (progress arc + square) instead of send. */
  isStreaming?: boolean
}

const SendButton = ({ onClick, disabled, isStreaming = false }: SendButtonProps) => {
  return (
    <IconButton
      aria-label={isStreaming ? 'Stop generating' : 'Send message'}
      onClick={onClick}
      size="sm"
      variant="ghost"
      borderRadius="full"
      disabled={disabled}
      minW={{ base: '36px', md: '32px' }}
      h={{ base: '36px', md: '32px' }}
      sx={{ touchAction: 'manipulation' }}
      // Stop keeps send's dark pill: it's the one live control while a reply
      // streams, so it must not read lighter (i.e. weaker) than the idle state.
      bg={disabled ? 'gray.100' : 'gray.800'}
      _hover={{
        bg: disabled ? 'gray.100' : 'gray.700',
        transform: disabled ? 'none' : 'scale(1.04)',
      }}
      _active={{ transform: disabled ? 'none' : 'scale(0.92)' }}
      _focusVisible={{ boxShadow: `0 0 0 2px white, 0 0 0 4px ${KRAKEN_PRIMARY}` }}
      css={{ transition: 'background-color 0.2s ease, transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      {isStreaming ? <StopGlyph /> : <ArrowUp size={17} strokeWidth={2.75} color={disabled ? 'rgba(0,0,0,0.25)' : 'white'} />}
    </IconButton>
  )
}

// Stop affordance: a rotating progress arc around a solid square. The arc is
// the only thing that moves, and it carries real meaning (still generating) —
// under reduced-motion it holds as a static full ring so the shape still reads.
const StopGlyph = () => (
  <Box position="relative" w="20px" h="20px" display="flex" alignItems="center" justifyContent="center">
    <Box
      as="svg"
      position="absolute"
      inset={0}
      viewBox="0 0 20 20"
      animation="kraken-stop-spin 0.9s linear infinite"
      css={{
        // Pin the pivot to the viewBox centre in user units. Browsers already
        // centre a root <svg>'s rotation, but stating it keeps the arc concentric
        // regardless of transform-box defaults.
        transformOrigin: '10px 10px',
        '@keyframes kraken-stop-spin': { to: { transform: 'rotate(360deg)' } },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* Track keeps the ring's full circle readable; the arc rides on top. */}
      <circle cx="10" cy="10" r="8.5" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="2" />
      <circle
        cx="10"
        cy="10"
        r="8.5"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="14 40"
      />
    </Box>
    <Box w="7px" h="7px" borderRadius="1.5px" bg="white" />
  </Box>
)

export default KrakenChatBar
export { KRAKEN_GRADIENT, KRAKEN_PRIMARY, KRAKEN_SECONDARY }
